import { cva, type VariantProps } from 'class-variance-authority';
import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

/**
 * `tone` cva variant added in Batch 9B Commit 2 — onboarding consumes
 * `'dark'` (the original visual on the navy OnboardingLayout backdrop),
 * Account routes consume `'light'` (against the white-card backdrop).
 * The popup body itself stays `bg-dream-white text-dream-navy` for
 * both tones — only the trigger button + focus ring flip.
 */
const triggerVariants = cva(
  'inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors focus:outline-none focus-visible:ring-2',
  {
    variants: {
      tone: {
        dark: 'bg-white/15 hover:bg-white/25 text-dream-white focus-visible:ring-dream-gold',
        light:
          'bg-dream-navy/10 hover:bg-dream-navy/20 text-dream-navy focus-visible:ring-dream-navy',
      },
    },
    defaultVariants: { tone: 'dark' },
  },
);

interface Props extends VariantProps<typeof triggerVariants> {
  text: string;
  className?: string;
}

export function InfoTooltip({ text, tone, className }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={STRINGS.children.fields.infoTooltipAriaLabel}
        aria-expanded={open}
        className={triggerVariants({ tone })}
      >
        <Info className="w-3 h-3" strokeWidth={2.5} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-full mt-2 z-10 w-64 max-w-[calc(100vw-3rem)] p-3 rounded-lg bg-dream-white text-dream-navy text-xs leading-relaxed shadow-lg"
        >
          {text}
        </div>
      )}
    </div>
  );
}
